import React, { useEffect, useState } from "react";

import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import {
  environmentSelector,
  environmentLoadingSelector
} from "Reducers/environment";
import { isLoadingSelector } from "Reducers/environment/deployment";
import { useLoadDeploymentsCurrent } from "Reducers/environment/deployment/hooks";
import { servicesSelector } from "Reducers/environment/service/service.selectors";
import { getAutoscalingConfigurationThunk } from "Reducers/environment/service/service.slice";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import EmptyTree from "./EmptyTree";
import List from "./ServicesList";
import Tree from "./ServicesTree/ServiceGraph";
import * as S from "./styles";

type ServicesDisplayProps = {
  hasInfoTooltip?: boolean;
  listMode?: boolean;
  mainEnvironmentId?: string;
  strokeColor?: string;
  width?: number | string;
  onLoadEnd?: () => void;
};

const ServicesDisplay = ({
  hasInfoTooltip,
  listMode,
  mainEnvironmentId,
  strokeColor,
  width,
  onLoadEnd
}: ServicesDisplayProps) => {
  const navigate = useNavigate();

  const {
    appName,
    environmentId,
    organizationId,
    projectId,
    "*": tabName
  } = useDecodedParams<{
    appName?: string;
    environmentId?: string;
    organizationId: string;
    projectId: string;
    "*"?: string;
  }>();

  const envId = environmentId || mainEnvironmentId;

  const [scrollLeft, setScrollLeft] = useState(0);
  const [isMouseDown, setMouseDown] = useState(false);
  const [startCursorX, setStartCursorX] = useState<false | number>(false);
  const [layoutRef, setLayoutRef] = useState<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();

  const environment = useSelectorWithUrlParams(environmentSelector, {
    environmentId: envId
  });
  const isEnvironmentLoading = useSelectorWithUrlParams(
    environmentLoadingSelector,
    {
      environmentId: envId
    }
  );

  useEffect(() => {
    if (projectId && environmentId) {
      dispatch(getAutoscalingConfigurationThunk({ projectId, environmentId }));
    }
  }, [dispatch, projectId, environmentId]);

  const autoscalingConfiguration = useAppSelector(servicesSelector);

  const { currentDeployment, currentDeploymentError } =
    useLoadDeploymentsCurrent(
      { organizationId, projectId, environmentId: envId! },
      !!environment?.has_code
    );

  const isDeploymentLoading = useSelectorWithUrlParams(isLoadingSelector);

  useEffect(() => {
    if (!onLoadEnd) return;
    if (
      currentDeploymentError ||
      (!isEnvironmentLoading && !environment?.has_code) ||
      (!isDeploymentLoading && !currentDeployment)
    )
      onLoadEnd();
  }, [
    currentDeploymentError,
    isEnvironmentLoading,
    environment,
    onLoadEnd,
    isDeploymentLoading,
    currentDeployment
  ]);

  const openNode = node => {
    if (hasInfoTooltip) return;
    goToService(node.metadata);
  };

  const goToService = meta => {
    // appName for worker is format like appName--workerName
    if (!envId) return;
    const service = meta.worker ? meta.appName.split("--")[0] : meta.appName;
    const env = encodeURIComponent(envId);
    let servicTabURL = `/${organizationId}/${projectId}/${env}/services/${service}`;
    if (tabName) servicTabURL += `/${tabName}`;
    navigate(servicTabURL);
  };

  const onMouseDown = e => {
    e.preventDefault();
    setStartCursorX(e.clientX);
    setMouseDown(true);
  };

  const onMouseUp = e => {
    e.preventDefault();
    setStartCursorX(false);
    setMouseDown(false);
  };

  const onMouseMove = e => {
    e.preventDefault();
    if (!startCursorX || isMouseDown) {
      return false;
    }

    const endCursorX = e.clientX;
    setStartCursorX(endCursorX);
    if (layoutRef?.scrollLeft) {
      layoutRef.scrollLeft = scrollLeft + (startCursorX - endCursorX);
    }
  };

  const onMouseOut = e => {
    e.preventDefault();
    onMouseMove(e);
    onMouseUp(e);
  };

  if (!isEnvironmentLoading && !environment?.has_code) {
    return (
      <EmptyTree>
        <FormattedMessage id="sevices.display.no_services" />
      </EmptyTree>
    );
  } else if (
    currentDeploymentError ||
    (!isDeploymentLoading && !currentDeployment)
  ) {
    return (
      <EmptyTree>
        <FormattedMessage id="services.display.error" />
      </EmptyTree>
    );
  }

  return (
    <S.Layout
      onScroll={e => {
        setScrollLeft(e.currentTarget.scrollLeft);
      }}
      onMouseUp={e => {
        onMouseUp(e);
      }}
      onMouseDown={e => {
        onMouseDown(e);
      }}
      onMouseMove={e => onMouseMove(e)}
      onMouseOut={e => {
        onMouseOut(e);
      }}
      ref={setLayoutRef}
      onBlur={e => {
        onMouseOut(e);
      }}
      listMode={listMode}
      data-testid="services-display"
    >
      {listMode ? (
        <List
          onClick={node => {
            openNode(node);
          }}
          currentDeployment={currentDeployment}
          search=""
          selected={appName || "routes"}
          autoscalingConfig={autoscalingConfiguration}
        />
      ) : (
        <Tree
          onClick={node => {
            openNode(node);
          }}
          goToService={goToService}
          strokeColor={strokeColor}
          width={width}
          hasInfoTooltip={hasInfoTooltip}
          onLoadEnd={onLoadEnd}
        />
      )}
    </S.Layout>
  );
};

export default ServicesDisplay;
